import React from 'react';
import { classNames } from 'react-extras';
import './Hero.scss';
import { PrismicLink } from 'components/PrismicLink';
import { Arrow } from 'components/Icons';
import { LrButtonArrow } from 'components/LANDR/LrButtonArrow';
import { Grid, GridRow, GridCol } from 'components/UI/Grid';
import { PrismicHTML, PrismicFluidImage } from 'components/Prismic';
import {
    PrismicTitleFieldType,
    PrismicRichTextFieldType,
    PrismicKeyTextFieldType,
    PrismicLinkFieldType,
    BooleanOptionType,
    SliceContext,
    PrismicImageField,
    PrismicFluidImageField,
} from '../types';
import { SupportedPrismicSlicesEnum } from '../enums';
import { componentDidmountAnimation } from './Animations';

type HeroProps = {
    sliceContext: SliceContext;
    slice: {
        type: SupportedPrismicSlicesEnum.hero;
        primary: {
            hero_title: PrismicTitleFieldType;
            hero_description: PrismicRichTextFieldType;
            hero_alignment: 'Left' | 'Center';
            hero_image: PrismicImageField;
            hero_imageSharp: PrismicFluidImageField;
            hero_cta_label: PrismicKeyTextFieldType;
            hero_cta_link: PrismicLinkFieldType;
            hero_cta_label_secondary: PrismicKeyTextFieldType;
            hero_cta_link_secondary: PrismicLinkFieldType;
            hero_show_arrow: BooleanOptionType;
        };
    };
};

const smoothScroll = (top: number) =>
    window.scrollTo({
        top,
        behavior: 'smooth',
    });

class Hero extends React.Component<HeroProps> {
    heroElement: HTMLDivElement | null;

    scrollPastHero = () => {
        if (this.heroElement) {
            // The navbar is a different height on mobile than it is for desktop
            const navbarHeight = window.innerWidth <= 1024 ? 64 : 80;
            smoothScroll(this.heroElement.offsetTop + this.heroElement.offsetHeight - navbarHeight);
        }
    };

    componentDidMount() {
        componentDidmountAnimation();
    }

    render() {
        const { primary } = this.props.slice;
        const heroAlignmentClass = `Hero--${primary.hero_alignment.toLowerCase()}`;
        const heroClasses: string = classNames('Hero', {
            [heroAlignmentClass]: true,
        });
        const hasBothLinks = !!primary.hero_cta_link_secondary && !!primary.hero_cta_link;
        return (
            <div className={heroClasses} ref={ref => (this.heroElement = ref)}>
                <Grid className="Hero-content">
                    <GridRow horizontalAlign={'h-start'} verticalAlign={'v-center'}>
                        <GridCol colSize={`12`} offsetSize={'0'}>
                            <PrismicHTML content={primary.hero_title} className="Hero-content-title" />
                            <PrismicHTML content={primary.hero_description} className="Hero-content-description" />
                        </GridCol>
                        <GridCol colSize={`12`} offsetSize={'0'} className="Bloc-cta">
                            <div className="Hero-actions">
                                <GridRow
                                    horizontalAlign={'h-center'}
                                    verticalAlign={'v-center'}
                                    className="Row--marginNone"
                                >
                                    <GridCol mobileColSize={'12'} offsetSize={'0'} className="Col--paddingNone">
                                        <PrismicLink link={primary.hero_cta_link} slice={this.props.sliceContext}>
                                            <LrButtonArrow size="medium">{primary.hero_cta_label}</LrButtonArrow>
                                        </PrismicLink>
                                    </GridCol>
                                    <GridCol mobileColSize={'12'} offsetSize={'0'} className="paddingNone">
                                        {primary.hero_cta_link_secondary && (
                                            <div className={hasBothLinks ? 'second-link' : ''}>
                                                <PrismicLink
                                                    link={primary.hero_cta_link_secondary}
                                                    slice={this.props.sliceContext}
                                                >
                                                    <LrButtonArrow size="medium">
                                                        {primary.hero_cta_label_secondary}
                                                    </LrButtonArrow>
                                                </PrismicLink>
                                            </div>
                                        )}
                                    </GridCol>
                                </GridRow>
                                {primary.hero_show_arrow === 'Yes' && (
                                    <button
                                        className={hasBothLinks ? 'Hero-actions-arrow Two-link' : 'Hero-actions-arrow'}
                                        onClick={this.scrollPastHero}
                                        aria-label="Go to next section"
                                    >
                                        <Arrow />
                                    </button>
                                )}
                            </div>
                        </GridCol>
                    </GridRow>
                </Grid>

                <PrismicFluidImage image={primary.hero_imageSharp} fallback={primary.hero_image} fullscreen={true} />
            </div>
        );
    }
}

export { Hero, HeroProps };
